import { useAtomValue, useSetAtom } from "jotai";
import React, { type FC, useCallback, memo } from "react";
import type { ProcessedLyricLine } from "$/modules/segmentation/utils/segment-processing.ts";
import {
	previewLineAtom,
	selectedWordIdAtom,
	selectionPanOffsetAtom,
	timelineDragAtom,
} from "$/modules/spectrogram/states/dnd.ts";
import { syncTargetModeAtom } from "$/modules/settings/states/sync.ts";
import {
	editingTimeFieldAtom,
	selectedLinesAtom,
	selectedWordsAtom,
} from "$/states/main.ts";
import { DividerSegment } from "./DividerSegment.tsx";
import { GapSegment } from "./GapSegment.tsx";
import styles from "./LyricLineSegment.module.css";
import { LyricWordSegment } from "./LyricWordSegment.tsx";

interface LyricLineSegmentProps {
	line: ProcessedLyricLine;
	allLines: ProcessedLyricLine[];
	splitLayersEnabled: boolean;
	zoom: number;
}

export const LyricLineSegment: FC<LyricLineSegmentProps> = memo(({
	line,
	allLines,
	splitLayersEnabled,
	zoom,
}) => {
	const previewLine = useAtomValue(previewLineAtom);
	const setSelectedLines = useSetAtom(selectedLinesAtom);
	const setSelectedWords = useSetAtom(selectedWordsAtom);
	const setSelectedWordId = useSetAtom(selectedWordIdAtom);
	const editingTimeField = useAtomValue(editingTimeFieldAtom);
	const selectionPanOffset = useAtomValue(selectionPanOffsetAtom);
	const timelineDragAtomValue = useAtomValue(timelineDragAtom);
	const selectedLines = useAtomValue(selectedLinesAtom);
	const selectedWords = useAtomValue(selectedWordsAtom);
	const syncTargetMode = useAtomValue(syncTargetModeAtom);

	let displayLine: ProcessedLyricLine;
	if (previewLine && previewLine.id === line.id) {
		displayLine = previewLine;
	} else {
		displayLine = line;
	}

	const handleMouseDown = useCallback(
		(e: React.MouseEvent) => {
			if (editingTimeField) return;

			if (!displayLine) return;
			e.stopPropagation();

			const { id, segments } = displayLine;

			const isShift = e.shiftKey;

			if (isShift) {
				setSelectedLines((prev) => {
					const newSet = new Set(prev);
					newSet.add(id);
					return newSet;
				});
				const firstWord = segments.find((s) => s.type === "word");
				if (firstWord) {
					setSelectedWords((prev) => {
						const newSet = new Set(prev);
						newSet.add(firstWord.id);
						return newSet;
					});
					setSelectedWordId(firstWord.id);
				}
			} else {
				setSelectedLines(new Set([id]));
				// Select first word segment so edit/time windows highlight it
				const firstWord = segments.find((s) => s.type === "word");
				if (firstWord) {
					setSelectedWords(new Set([firstWord.id]));
					setSelectedWordId(firstWord.id);
				} else {
					setSelectedWords(new Set());
					setSelectedWordId(null);
				}
			}
		},
		[editingTimeField, displayLine, setSelectedLines, setSelectedWords, setSelectedWordId],
	);

	if (!displayLine) {
		return null;
	}

	const { startTime, endTime, segments } = displayLine;
	const segmentsLength = segments.length;

	if (startTime == null || endTime == null || endTime <= startTime) {
		return null;
	}

	const left = (startTime / 1000) * zoom;
	const width = ((endTime - startTime) / 1000) * zoom;

	if (width < 1) {
		return null;
	}

	const isTouchingStart = allLines.some(
		(l) => l.id !== line.id && l.endTime === startTime,
	);
	const isTouchingEnd = allLines.some(
		(l) => l.id !== line.id && l.startTime === endTime,
	);

	const hasSelectedWords = segments.some((s) => s.type === "word" && selectedWords.has(s.id));
	const isCurrentlySelected = selectedLines.has(line.id);
	const isSelectedByPan = isCurrentlySelected && !hasSelectedWords && timelineDragAtomValue?.type === "selection-pan";
	const effectiveLeft = isSelectedByPan ? left + (selectionPanOffset * zoom) / 1000 : left;

	const dynamicStyles = {
		left: `${effectiveLeft}px`,
		width: `${width}px`,
		cursor: "auto",
		...(splitLayersEnabled && {
			height: '50%',
			top: line.isBG ? '50%' : '0'
		})
	};

	return (
		// biome-ignore lint/a11y/useSemanticElements: <button> 不适用
		<div
			className={styles.lineSegment + (splitLayersEnabled ? ` ${styles.isSplitEnabled}` : '')}
			style={dynamicStyles}
			onMouseDown={handleMouseDown}
			tabIndex={0}
			role="button"
			aria-label="Lyric Line"
			data-line-id={displayLine.id}
		>
			<DividerSegment
				key="divider-start"
				lineId={displayLine.id}
				segmentIndex={-1}
				timeMs={startTime}
				lineStartTime={startTime}
				segmentsLength={segmentsLength}
				isTouching={isTouchingStart}
				zoom={zoom}
			/>

			{segments.map((segment, index) => (
				<React.Fragment key={segment.id}>
					{segment.type === "word" ? (
						<LyricWordSegment
							lineId={displayLine.id}
							segment={segment}
							lineStartTime={startTime}
							zoom={zoom}
						/>
					) : (
						<GapSegment
							lineId={displayLine.id}
							segment={segment}
							lineStartTime={startTime}
							zoom={zoom}
							text={
								syncTargetMode === "line" && segmentsLength === 1
									? displayLine.text
									: undefined
							}
						/>
					)}
					<DividerSegment
						key={`divider-${segment.id}`}
						lineId={displayLine.id}
						segmentIndex={index}
						timeMs={segment.endTime}
						lineStartTime={startTime}
						segmentsLength={segmentsLength}
						isTouching={index === segmentsLength - 1 ? isTouchingEnd : false}
						zoom={zoom}
					/>
				</React.Fragment>
			))}
		</div>
	);
});
